(() => {
  const TRIGGER_PRODUCT_ID = 9637001920809; // BuubiBottle Portable Milk Warmer PRO
  const GIFT_VARIANT_ID = 52911565963561; // BuubiBottle Mini, Orange / 2-Pack
  const GIFT_DISCOUNT_CODE = 'GWP-PROWARMER-MINI2PK';
  const GIFT_PROPERTY_KEY = '_gift_with_purchase';
  // Cart-level attributes (persist across cart:change events and page loads,
  // unlike a plain JS variable) used to tell "customer explicitly removed the
  // gift" apart from "we just haven't added it yet".
  const DECLINED_ATTR_KEY = '_gift_with_purchase_declined';
  const SYNCED_QTY_ATTR_KEY = '_gift_with_purchase_synced_qty';

  let syncing = false;

  const isEnabled = () => Boolean(window.themeVariables?.settings?.giftWithPurchaseEnabled);

  const isGiftLine = (item) =>
    item.variant_id === GIFT_VARIANT_ID && item.properties?.[GIFT_PROPERTY_KEY] === 'true';

  const triggerQuantity = (cart) =>
    cart.items
      .filter((item) => item.product_id === TRIGGER_PRODUCT_ID)
      .reduce((sum, item) => sum + item.quantity, 0);

  async function addGift(quantity) {
    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [
          {
            id: GIFT_VARIANT_ID,
            quantity,
            properties: { [GIFT_PROPERTY_KEY]: 'true' },
          },
        ],
        sections: sectionsToBundle.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const responseJson = await response.json();
    if (!response.ok) {
      console.error('gift-with-purchase: failed to add gift', responseJson);
      return null;
    }
    return responseJson;
  }

  async function setGiftQuantity(lineKey, quantity) {
    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: lineKey, quantity }),
    });

    if (!response.ok) {
      console.error('gift-with-purchase: failed to update gift quantity', await response.json());
    }
  }

  async function syncCartState(cart, { wantGift, declined, syncedQty }) {
    const existingCodes = (cart.discount_codes || [])
      .map((discount) => discount.code)
      .filter((code) => code.toLowerCase() !== GIFT_DISCOUNT_CODE.toLowerCase());
    const desiredCodes = wantGift ? [...existingCodes, GIFT_DISCOUNT_CODE] : existingCodes;
    const currentCodes = (cart.discount_codes || []).map((discount) => discount.code);

    const currentAttrs = cart.attributes || {};
    const desiredAttrs = {
      [DECLINED_ATTR_KEY]: declined ? 'true' : '',
      [SYNCED_QTY_ATTR_KEY]: String(syncedQty),
    };
    const attrsInSync =
      (currentAttrs[DECLINED_ATTR_KEY] || '') === desiredAttrs[DECLINED_ATTR_KEY] &&
      (currentAttrs[SYNCED_QTY_ATTR_KEY] || '0') === desiredAttrs[SYNCED_QTY_ATTR_KEY];

    if (desiredCodes.join(',') === currentCodes.join(',') && attrsInSync) return;

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ discount: desiredCodes.join(','), attributes: desiredAttrs }),
    });

    if (!response.ok) {
      console.error('gift-with-purchase: failed to sync discount code/attributes', await response.json());
    }
  }

  async function refreshCartUi(baseEvent) {
    const cartData = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent, cart: cartData },
      })
    );

    // cart:change here carries plain /cart.js data with no bundled section HTML,
    // which <cart-drawer> needs to actually re-render what's on screen (it expects
    // cart.sections from an /cart/add.js|change.js|update.js response). Without this,
    // the underlying cart is correct but the drawer can keep showing a stale gift line
    // after it's been removed. cart:refresh tells listeners to redo their own
    // ?section_id= fetch instead of relying on bundled sections.
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function handleCartChange(event) {
    if (syncing || !isEnabled()) return;

    const cart = event.detail?.cart;
    if (!cart) return;

    const triggerQty = triggerQuantity(cart);
    const giftItem = cart.items.find(isGiftLine);
    const currentGiftQty = giftItem ? giftItem.quantity : 0;
    const attrs = cart.attributes || {};
    const wasDeclined = attrs[DECLINED_ATTR_KEY] === 'true';
    const lastSyncedQty = parseInt(attrs[SYNCED_QTY_ATTR_KEY], 10) || 0;

    // No trigger product in cart at all: forget any prior decline so a fresh
    // add of the trigger product later offers the gift again from scratch.
    let declined = triggerQty === 0 ? false : wasDeclined;

    // The gift line dropped below what we last set it to (and it wasn't
    // already flagged as declined) — that can only mean the customer removed
    // or reduced it themselves via the cart's own remove/quantity control,
    // since every reduction *this script* makes is followed immediately by
    // recording the new synced qty. Respect that choice instead of re-adding.
    if (!wasDeclined && triggerQty > 0 && currentGiftQty < lastSyncedQty) {
      declined = true;
    }

    const desiredGiftQty = declined ? 0 : triggerQty;
    const codesInSync =
      (cart.discount_codes || []).some((d) => d.code.toLowerCase() === GIFT_DISCOUNT_CODE.toLowerCase()) ===
      desiredGiftQty > 0;
    const attrsInSync = declined === wasDeclined && lastSyncedQty === currentGiftQty;

    if (desiredGiftQty === currentGiftQty && codesInSync && attrsInSync) return;

    syncing = true;
    try {
      if (desiredGiftQty > currentGiftQty) {
        const added = await addGift(desiredGiftQty - currentGiftQty);
        if (added) {
          await syncCartState(cart, { wantGift: true, declined, syncedQty: desiredGiftQty });
        } else {
          console.error('gift-with-purchase: gift item failed to add (see error above) — leaving any existing discount code as-is rather than applying it against a gift that was never added.');
        }
      } else if (desiredGiftQty < currentGiftQty) {
        await setGiftQuantity(giftItem.key, desiredGiftQty);
        await syncCartState(cart, { wantGift: desiredGiftQty > 0, declined, syncedQty: desiredGiftQty });
      } else {
        await syncCartState(cart, { wantGift: desiredGiftQty > 0, declined, syncedQty: desiredGiftQty });
      }
      await refreshCartUi('line-item:change');
    } finally {
      syncing = false;
    }
  }

  document.addEventListener('cart:change', handleCartChange);
})();
